import { Button, Logo, cn } from "@exhousing/design-system";
import {
  Building,
  Building2,
  CalendarCheck,
  ClipboardCheck,
  Home,
  Inbox,
  LayoutDashboard,
  LogOut,
  Mail,
  MailQuestion,
  Menu,
  Receipt,
  Search,
  Send,
  Settings,
  Target,
  Users,
  X,
} from "lucide-react";
import type { ComponentType, ReactNode } from "react";
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";

import { CommandPalette } from "@/components/CommandPalette";
import { isLandlord } from "@/lib/roles";
import { useLogout, useMe } from "@/hooks/api/useAuth";

type Me = ReturnType<typeof useMe>["data"];
type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  show?: (me: Me) => boolean;
};
type NavSection = { label: string; items: NavItem[] };

const canManageUsers = (me: Me) =>
  Boolean(me?.permissions?.includes("user.view") || me?.roles?.includes("super_admin"));
const isSuperAdmin = (me: Me) => Boolean(me?.roles?.includes("super_admin"));
const canViewApplications = (me: Me) =>
  Boolean(me?.permissions?.includes("application.view") || me?.roles?.includes("super_admin"));
const canViewInbox = (me: Me) =>
  Boolean(me?.permissions?.includes("inbox.view") || me?.roles?.includes("super_admin"));
const canViewBilling = (me: Me) =>
  Boolean(me?.permissions?.includes("billing.view") || me?.roles?.includes("super_admin"));
const canViewDocuments = (me: Me) =>
  Boolean(me?.permissions?.includes("document.view") || me?.roles?.includes("super_admin"));

// Landlord-portal guests (journey 1.1): a deliberately calm, minimal navigation.
const LANDLORD_SECTIONS: NavSection[] = [
  {
    label: "Portal",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/properties", label: "Properties", icon: Home },
      { to: "/applications", label: "Candidates", icon: MailQuestion },
      { to: "/contracts", label: "Contracts", icon: Building2 },
    ],
  },
];

const SECTIONS: NavSection[] = [
  // The core operating loop, in deal-flow order — this is what the system is for.
  {
    label: "Workspace",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/properties", label: "Properties", icon: Home },
      { to: "/applications", label: "Applications", icon: MailQuestion, show: canViewApplications },
      { to: "/viewings", label: "Viewings", icon: CalendarCheck },
      { to: "/contracts", label: "Contracts", icon: Building2 },
      { to: "/inspections", label: "Inspections", icon: ClipboardCheck },
    ],
  },
  {
    // Ledger is accounting detail — reachable from the contract Billing tab and ⌘K.
    label: "Finance",
    items: [{ to: "/invoices", label: "Invoices", icon: Receipt, show: canViewBilling }],
  },
  {
    label: "Marketing",
    items: [
      { to: "/subscribers", label: "Subscribers", icon: Mail },
      { to: "/mailings", label: "Mailings", icon: Send },
      { to: "/crm", label: "CRM", icon: Target },
      { to: "/inbox", label: "Inbox", icon: Inbox, show: canViewInbox },
    ],
  },
  {
    label: "Directory",
    items: [{ to: "/parties", label: "Landlords & Tenants", icon: Users }],
  },
  {
    // The four config surfaces collapse into one hub (see SettingsPage).
    label: "Settings",
    items: [
      {
        to: "/settings",
        label: "Settings",
        icon: Settings,
        show: (me) => canViewDocuments(me) || canManageUsers(me),
      },
      { to: "/agencies", label: "Agencies", icon: Building, show: isSuperAdmin },
    ],
  },
];

function SidebarNav({
  sections,
  onNavigate,
}: {
  sections: NavSection[];
  onNavigate?: () => void;
}) {
  return (
    <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
      {sections.map((section) => (
        <div key={section.label}>
          <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            {section.label}
          </p>
          <div className="space-y-0.5">
            {section.items.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )
                }
              >
                <Icon className="size-4 shrink-0" />
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export function AppLayout({ children }: { children: ReactNode }) {
  const { data: me } = useMe();
  const logout = useLogout();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const initials = me
    ? `${me.first_name?.[0] ?? ""}${me.last_name?.[0] ?? ""}`.toUpperCase()
    : "";

  const sections = (isLandlord(me) ? LANDLORD_SECTIONS : SECTIONS)
    .map((s) => ({
      ...s,
      items: s.items.filter((i) => !i.show || i.show(me)),
    }))
    .filter((s) => s.items.length > 0);

  // Close the mobile drawer on Escape, and lock background scroll while it's open.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMobileNavOpen(false);
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  return (
    <div className="flex h-full bg-background">
      {/* Sidebar (desktop) */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-card/60 md:flex">
        <div className="flex h-16 items-center px-5">
          <Logo />
        </div>
        <SidebarNav sections={sections} />
        <div className="border-t border-border p-3 text-xs text-muted-foreground">
          Balatin Operations
        </div>
      </aside>

      {/* Navigation drawer (mobile) */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden
          />
          <aside className="absolute inset-y-0 left-0 flex w-72 max-w-[82%] flex-col border-r border-border bg-card shadow-xl">
            <div className="flex h-16 items-center justify-between px-5">
              <Logo />
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close menu"
                className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="size-5" />
              </button>
            </div>
            <SidebarNav sections={sections} onNavigate={() => setMobileNavOpen(false)} />
            <div className="border-t border-border p-3 text-xs text-muted-foreground">
              Balatin Operations
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-card/40 px-4 sm:px-6">
          <div className="flex items-center gap-2 md:hidden">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open menu"
              aria-expanded={mobileNavOpen}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Menu className="size-5" />
            </button>
            <Logo showWordmark={false} />
          </div>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("open-command-palette"))}
            className="ml-4 hidden items-center gap-2 rounded-md border border-border bg-card/60 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground sm:flex"
          >
            <Search className="size-4" />
            <span>Search…</span>
            <kbd className="rounded border border-border px-1.5 py-0.5 text-[10px]">
              ⌘K
            </kbd>
          </button>
          <div className="ml-auto flex items-center gap-4">
            {me && (
              <div className="flex items-center gap-3">
                <div className="hidden text-right sm:block">
                  <p className="text-sm font-medium leading-tight">
                    {me.first_name} {me.last_name}
                  </p>
                  <p className="text-xs text-muted-foreground">{me.email}</p>
                </div>
                <div className="flex size-9 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
                  {initials}
                </div>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => logout.mutate()}
              aria-label="Sign out"
            >
              <LogOut className="size-4" />
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-6 py-8">
          <div className="mx-auto max-w-6xl animate-fade-in-up">{children}</div>
        </main>
      </div>

      <CommandPalette />
    </div>
  );
}
